import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { faFile, faFolderOpen, faClock, faInbox, faUserMinus, faArchive } from '@fortawesome/free-solid-svg-icons';
import { DocumentService, sharedApiClient } from '../fetcher';
import {
  IdentityService,
  TeamService,
  useNotifications,
  useAuth,
  UserAbility,
  getAssigneeName as resolveAssigneeName,
  createCoalescer,
  isAbortError,
} from '@medprompt/shared';
import {
  DocumentViewMode,
  documentBelongsInView,
  mergeFetchedDocument,
  shouldFetchDocumentUpdate,
  shouldRefreshSearch,
} from './documentListMembership';

export { DocumentViewMode };

const VIEW_MODE_STORAGE_KEY = 'documentsViewMode';

const useDropdown = (initialValue) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState(initialValue);
  const dropdownRef = useRef(null);

  const toggle = (otherDropdowns) => {
    setIsOpen(!isOpen);
    otherDropdowns.forEach(dropdown => dropdown.setIsOpen(false));
  };

  const handleSelect = (option) => {
    setSelected(option);
    setIsOpen(false);
  };

  return {
    isOpen,
    setIsOpen,
    selected,
    setSelected,
    toggle,
    handleSelect,
    dropdownRef
  };
};

/** The rows for the current view mode or search, kept current from the update stream. */
const useDocumentList = () => {
  const { user } = useAuth();
  const { addDocumentUpdateCallback, addReconnectCallback } = useNotifications();
  const documentService = useMemo(() => new DocumentService(sharedApiClient), []);

  const [documents, setDocuments] = useState([]);
  const [activeUsers, setActiveUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [teamsReady, setTeamsReady] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [clearSearchBarFunction, setClearSearchBarFunction] = useState(null);
  const listAbortRef = useRef(null);
  const patchAbortRef = useRef(null);
  const searchAbortRef = useRef(null);
  const searchRefreshAbortRef = useRef(null);
  const rerunSearchRef = useRef(() => {});
  // Ids with a list_data fetch in flight or not yet committed. A follow-up event
  // for one of these still fetches, so it cannot be dropped before the row lands.
  const patchIdsRef = useRef(new Set());

  const hasDocsReadAll = Boolean(user?.authorities?.includes(UserAbility.DOCS_READ_ALL.key));

  // Initialize view mode from session storage, default based on permissions
  const [viewMode, setViewMode] = useState(() => {
    const stored = sessionStorage.getItem(VIEW_MODE_STORAGE_KEY);
    if (stored && Object.values(DocumentViewMode).includes(stored)) {
      return stored;
    }
    return hasDocsReadAll ? DocumentViewMode.ALL_DOCUMENTS : DocumentViewMode.MY_DOCUMENTS;
  });

  useEffect(() => {
    sessionStorage.setItem(VIEW_MODE_STORAGE_KEY, viewMode);
  }, [viewMode]);

  // Full list fetch. Reserved for mount, view-mode change, and reconnect.
  // A live update patches one row instead.
  // Deliberately has no "already fetching" guard. Such a guard would drop the newest request while
  // keeping the one the abort below has just cancelled, leaving the list permanently empty. Newest
  // request wins; the identity check before the state write discards anything superseded.
  const refreshDocuments = useCallback(async () => {
    // A full reload supersedes any in-flight row patch, including one started under the previous view.
    patchIdsRef.current.clear();
    patchAbortRef.current?.abort();
    // A superseded list fetch is dead weight on a request thread; drop it.
    listAbortRef.current?.abort();
    const controller = new AbortController();
    listAbortRef.current = controller;
    const options = { signal: controller.signal };

    try {
      const canReadAll = user?.authorities?.includes(UserAbility.DOCS_READ_ALL.key);
      let docs;

      switch (viewMode) {
        case DocumentViewMode.MY_DOCUMENTS:
          docs = await documentService.fetchYourDocuments(options);
          break;

        case DocumentViewMode.ALL_DOCUMENTS:
          // Only users with DOCS_READ_ALL can fetch active documents
          docs = canReadAll
            ? await documentService.fetchActiveDocuments(options)
            : await documentService.fetchYourDocuments(options);
          break;

        case DocumentViewMode.PENDING_DOCUMENTS:
          docs = canReadAll
            ? await documentService.fetchPendingDocuments(options)
            : await documentService.fetchYourDocuments(options);
          break;

        case DocumentViewMode.UNASSIGNED_DOCUMENTS:
          docs = canReadAll
            ? await documentService.fetchUnassignedDocuments(options)
            : await documentService.fetchYourDocuments(options);
          break;

        case DocumentViewMode.UNMATCHED_DOCUMENTS:
          docs = canReadAll
            ? await documentService.fetchUnmatchedDocuments(options)
            : await documentService.fetchYourDocuments(options);
          break;

        case DocumentViewMode.ARCHIVE:
          docs = await documentService.fetchArchiveDocuments(options);
          break;

        default:
          console.warn(`Unknown view mode: ${viewMode}, defaulting to MY_DOCUMENTS`);
          docs = await documentService.fetchYourDocuments(options);
      }

      if (listAbortRef.current !== controller) {
        return; // a newer fetch has taken over
      }
      setDocuments(Array.isArray(docs) ? docs : []);
      // Delete and other explicit refreshes land here. While a search is showing,
      // that list has to be re-run too, or the rows on screen stay stale.
      rerunSearchRef.current();
    } catch (err) {
      if (!isAbortError(err)) {
        console.error('Error refreshing documents:', err);
      }
    } finally {
      if (listAbortRef.current === controller) {
        listAbortRef.current = null;
      }
    }
  }, [documentService, user, viewMode]);

  useEffect(() => {
    const identityManager = new IdentityService();
    const teamManager = new TeamService();

    identityManager.fetchActiveClinicUsers()
      .then((users) => {
        setActiveUsers(users || []);
      })
      .catch((error) => {
        console.error('Failed to fetch active users:', error);
        setActiveUsers([]);
      });

    teamManager.fetchTeams()
      .then((teamsData) => {
        setTeams(teamsData || []);
        setTeamsReady(true);
      })
      .catch((error) => {
        console.error('Failed to fetch teams:', error);
        setTeams([]);
      });
  }, []);

  // Covers mount as well as every view-mode change; a second mount-only effect would just
  // duplicate the first fetch.
  useEffect(() => {
    refreshDocuments();
  }, [viewMode, refreshDocuments]);

  useEffect(() => () => {
    listAbortRef.current?.abort();
    patchAbortRef.current?.abort();
    searchAbortRef.current?.abort();
    searchRefreshAbortRef.current?.abort();
  }, []);

  // Read during notification handling, which is subscribed once. Assigning during render
  // keeps a burst of events on the list and view that are actually on screen.
  const listSyncRef = useRef({
    documents,
    viewMode,
    userId: null,
    teams,
    teamsReady,
    searchResults,
    isSearchActive,
    searchQuery,
  });
  listSyncRef.current = {
    documents,
    viewMode,
    userId: user?.userId ?? null,
    teams,
    teamsReady,
    searchResults,
    isSearchActive,
    searchQuery,
  };

  // One list_data fetch per id. The view predicate then keeps, inserts, or removes the row.
  // A full reload is reserved for view changes and reconnect.
  const patchDocuments = useCallback(async (documentIds) => {
    patchAbortRef.current?.abort();
    const controller = new AbortController();
    patchAbortRef.current = controller;

    const results = await Promise.all(documentIds.map(async (documentId) => {
      try {
        return await documentService.fetchDocumentListData(documentId, { signal: controller.signal });
      } catch (error) {
        if (isAbortError(error)) {
          return undefined;
        }
        // Gone from under us, or no longer visible to this user: treat as a removal.
        return null;
      }
    }));

    if (controller.signal.aborted) {
      return;
    }
    if (patchAbortRef.current === controller) {
      patchAbortRef.current = null;
    }

    const { viewMode: mode, userId, teams: memberTeams, teamsReady: memberTeamsReady, documents: shown } = listSyncRef.current;
    const context = { userId, teams: memberTeams, teamsReady: memberTeamsReady };
    const decisions = documentIds.map((documentId, index) => {
      const fetched = results[index];
      if (fetched === undefined) {
        return { documentId, skip: true };
      }
      const belongs = fetched == null ? false : documentBelongsInView(fetched, mode, context);
      return { documentId, fetched, belongs, skip: false };
    });

    setDocuments(previous => {
      let next = previous;
      decisions.forEach((decision) => {
        if (decision.skip) {
          return;
        }
        next = mergeFetchedDocument(next, decision.documentId, decision.fetched, decision.belongs);
      });
      return next;
    });

    // A row that will not be shown can leave the in-flight set now. A row that will
    // be shown stays tracked until the list state contains it, so an event that
    // arrives before that commit still fetches.
    decisions.forEach((decision) => {
      if (decision.skip) {
        return;
      }
      const stays = decision.belongs === true
        || (decision.belongs == null && shown.some(doc => doc.documentId === decision.documentId));
      if (!stays) {
        patchIdsRef.current.delete(decision.documentId);
      }
    });
  }, [documentService]);

  // The coalescer is created once and reads the latest handler through a ref, so a patch landing
  // does not tear down the batch that produced it.
  const patchHandlerRef = useRef(patchDocuments);
  useEffect(() => {
    patchHandlerRef.current = patchDocuments;
  }, [patchDocuments]);

  useEffect(() => {
    const ids = patchIdsRef.current;
    if (ids.size === 0) {
      return;
    }
    for (const id of ids) {
      if (documents.some(doc => doc.documentId === id)) {
        ids.delete(id);
      }
    }
  }, [documents]);

  const patchCoalescerRef = useRef(null);
  const searchCoalescerRef = useRef(null);
  useEffect(() => {
    patchCoalescerRef.current = createCoalescer((ids) => patchHandlerRef.current(ids));
    searchCoalescerRef.current = createCoalescer(() => rerunSearchRef.current());
    return () => {
      patchCoalescerRef.current?.cancel();
      searchCoalescerRef.current?.cancel();
    };
  }, []);

  // Act on the id in the event instead of reloading everything
  useEffect(() => {
    if (!addDocumentUpdateCallback) return undefined;

    const unsubscribe = addDocumentUpdateCallback((documentId, statusTransition) => {
      if (!documentId) {
        return;
      }
      if (statusTransition?.to === 'NON_EXISTENT') {
        patchIdsRef.current.delete(documentId);
        setDocuments(previous => previous.filter(doc => doc.documentId !== documentId));
        setSearchResults(previous => previous.filter(doc => doc.documentId !== documentId));
        return;
      }
      const { documents: shown, viewMode: mode, searchResults: results, isSearchActive: searching } = listSyncRef.current;
      const isTracked = shown.some(doc => doc.documentId === documentId)
        || patchIdsRef.current.has(documentId);
      if (shouldFetchDocumentUpdate(isTracked, statusTransition, mode)) {
        patchIdsRef.current.add(documentId);
        patchCoalescerRef.current?.push(documentId);
      }
      const inSearch = results.some(doc => doc.documentId === documentId);
      if (searching && shouldRefreshSearch(inSearch, statusTransition)) {
        searchCoalescerRef.current?.push(documentId);
      }
    });

    return () => unsubscribe();
  }, [addDocumentUpdateCallback]);

  // Anything that changed while the connection was down has to come from a full fetch
  useEffect(() => {
    if (!addReconnectCallback) return undefined;
    return addReconnectCallback(() => refreshDocuments());
  }, [addReconnectCallback, refreshDocuments]);

  const getAssigneeName = useCallback((assigneeId) => (
    resolveAssigneeName(assigneeId, activeUsers, teams)
  ), [activeUsers, teams]);

  // Re-run the query on screen. A live update cannot tell whether some other document
  // now matches, so the search itself is the membership check. One burst becomes one request.
  const rerunSearch = useCallback(async () => {
    const { isSearchActive: searching, searchQuery: query } = listSyncRef.current;
    if (!searching || !query?.trim()) {
      return;
    }

    searchRefreshAbortRef.current?.abort();
    const controller = new AbortController();
    searchRefreshAbortRef.current = controller;

    try {
      const results = await documentService.searchDocuments(query, { signal: controller.signal });
      if (searchRefreshAbortRef.current !== controller) {
        return;
      }
      const current = listSyncRef.current;
      if (!current.isSearchActive || current.searchQuery !== query) {
        return;
      }
      setSearchResults(Array.isArray(results) ? results : []);
    } catch (error) {
      if (!isAbortError(error)) {
        console.error('Error refreshing search results:', error);
      }
    } finally {
      if (searchRefreshAbortRef.current === controller) {
        searchRefreshAbortRef.current = null;
      }
    }
  }, [documentService]);
  rerunSearchRef.current = rerunSearch;

  // Typing faster than the server responds leaves several searches in flight, and without this the
  // last to *arrive* wins rather than the last typed. Cancelling the previous one keeps the results
  // matching the query on screen, and stops the server working on answers nobody will see.
  const handleDocumentSearch = useCallback(async (query, callback) => {
    searchAbortRef.current?.abort();
    searchRefreshAbortRef.current?.abort();

    if (!query || !query.trim()) {
      searchAbortRef.current = null;
      setSearchResults([]);
      setIsSearchActive(false);
      setSearchQuery('');
      if (callback) {
        callback([]);
      }
      return;
    }

    const controller = new AbortController();
    searchAbortRef.current = controller;

    try {
      const results = await documentService.searchDocuments(query, { signal: controller.signal });
      if (searchAbortRef.current !== controller) {
        return; // a newer query has taken over
      }
      setSearchResults(Array.isArray(results) ? results : []);
      setIsSearchActive(true);
      setSearchQuery(query);
      if (callback) {
        callback(results);
      }
    } catch (error) {
      if (isAbortError(error)) {
        return; // superseded; the newer query owns the results
      }
      console.error('Error searching documents:', error);
      setSearchResults([]);
      setIsSearchActive(false);
      setSearchQuery('');
      if (callback) {
        callback([]);
      }
    } finally {
      if (searchAbortRef.current === controller) {
        searchAbortRef.current = null;
      }
    }
  }, [documentService]);

  // Clear search results and return to the list for the view mode
  const handleClearSearch = () => {
    searchRefreshAbortRef.current?.abort();
    setSearchResults([]);
    setIsSearchActive(false);
    setSearchQuery('');
    if (clearSearchBarFunction) {
      clearSearchBarFunction();
    }
  };

  const viewModeOptions = useMemo(() => {
    const options = [
      { value: DocumentViewMode.MY_DOCUMENTS, name: 'My Docs', icon: faFile }
    ];

    if (hasDocsReadAll) {
      options.push({ value: DocumentViewMode.PENDING_DOCUMENTS, name: 'Pending Docs', icon: faClock });
      options.push({ value: DocumentViewMode.UNASSIGNED_DOCUMENTS, name: 'Unassigned Docs', icon: faInbox });
      options.push({ value: DocumentViewMode.UNMATCHED_DOCUMENTS, name: 'Unmatched Docs', icon: faUserMinus });
      options.push({ value: DocumentViewMode.ALL_DOCUMENTS, name: 'All Docs', icon: faFolderOpen });
    }

    options.push({ value: DocumentViewMode.ARCHIVE, name: 'Archive', icon: faArchive });

    return options;
  }, [hasDocsReadAll]);

  const currentViewModeOption = viewModeOptions.find(opt => opt.value === viewMode);
  const currentViewModeName = currentViewModeOption ? currentViewModeOption.name : 'My Docs';
  const currentViewModeIcon = currentViewModeOption ? currentViewModeOption.icon : faFile;
  const viewModeDropdown = useDropdown(currentViewModeName);

  const selectViewMode = (option) => {
    setViewMode(option.value);
    viewModeDropdown.handleSelect(option.name);
  };

  // Search results replace the list. Otherwise hide a row the view predicate rejects.
  // Null means team membership is still unknown, and that row stays.
  const visibleDocuments = useMemo(() => {
    if (isSearchActive) {
      return searchResults;
    }
    const context = { userId: user?.userId ?? null, teams, teamsReady };
    return documents.filter(doc => documentBelongsInView(doc, viewMode, context) !== false);
  }, [documents, searchResults, isSearchActive, viewMode, user, teams, teamsReady]);

  return {
    documentService,
    user,
    hasDocsReadAll,
    documents,
    visibleDocuments,
    refreshDocuments,
    activeUsers,
    teams,
    getAssigneeName,
    viewMode,
    viewModeOptions,
    viewModeDropdown,
    currentViewModeName,
    currentViewModeIcon,
    selectViewMode,
    isSearchActive,
    searchResults,
    searchQuery,
    setClearSearchBarFunction,
    handleDocumentSearch,
    handleClearSearch,
  };
};

export default useDocumentList;
